import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  IconButton,
  Box,
  Modal,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCreatePayment } from "../../requests/create-payment";
import { useNavigate } from "react-router-dom";

const MIN_AMOUNT = 50;

const CreatePayment: React.FC = () => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const navigate = useNavigate();

  const { mutateAsync } = useCreatePayment();
  const [isPaid, setIsPaid] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [paymentData, setPaymentData] = useState<{
    transactionId: string;
    code: string;
    qrcode: string;
  } | null>(null);

  const [openModal, setOpenModal] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const transactionId = useMemo(() => {
    return paymentData?.transactionId ?? "";
  }, [paymentData?.transactionId]);

  const handleConfirm = useCallback(async () => {
    if (amount < MIN_AMOUNT) {
      setError(`O valor mínimo é R$ ${MIN_AMOUNT}`);
      return;
    }

    try {
      const data = await mutateAsync({ amount });
      setPaymentData(data);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Erro ao processar o pagamento.");
    }
  }, [amount, mutateAsync]);

  const handleCopyCode = useCallback(() => {
    if (paymentData?.code) {
      navigator.clipboard.writeText(paymentData.code);
      alert("Código PIX copiado para a área de transferência!");
    }
  }, [paymentData?.code]);

  useEffect(() => {
    if (!transactionId) return;

    const eventSource = new EventSource(
      `https://financial-api.ms.leveai.com.br/events/${transactionId}`
    );

    eventSourceRef.current = eventSource;
    eventSource.onmessage = (event) => {
      const { status } = JSON.parse(event.data);

      if (status === "PAID") {
        setIsPaid(true);
        eventSource.close();
        eventSourceRef.current = null;
      }
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [transactionId]);

  useEffect(() => {
    if (isPaid) {
      setOpenModal(true);
      setCountdown(10); // Inicializa a contagem regressiva em 10

      const interval = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(interval);
            setOpenModal(false);
            navigate("/payment");
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isPaid, navigate]);

  return (
    <Container maxWidth="sm">
      <Modal open={openModal} onClose={() => {}} aria-labelledby="modal-title">
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            Pagamento Realizado com Sucesso!
          </Typography>
          <Typography variant="body1">
            Você será redirecionado para o dashboard em {countdown}{" "}
            {countdown === 1 ? "segundo" : "segundos"}.
          </Typography>
        </Box>
      </Modal>

      {!paymentData ? (
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Adicionar Saldo
          </Typography>
          <TextField
            label="Valor"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            placeholder="Digite o valor"
            margin="normal"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            fullWidth
            sx={{ mt: 3 }}
          >
            Confirmar
          </Button>
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: "center" }}>
          <Typography variant="h5" gutterBottom>
            Pagamento PIX
          </Typography>
          <Box sx={{ my: 4 }}>
            <img
              src={`${paymentData.qrcode}`}
              alt="QR Code PIX"
              width={200}
              height={200}
            />
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <TextField
                label="Código PIX"
                fullWidth
                value={paymentData.code}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item>
              <IconButton color="primary" onClick={handleCopyCode}>
                <ContentCopyIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export { CreatePayment };
